import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import type { AIPurchaseAnalyticsState } from '../../types/api.types';
import {
    fetchAIPurchaseByState,
    useAIPurchaseStates,
    useAIPurchaseLoading,
    useAIPurchaseError,
} from '../../store/slices/aiPurchaseAnalyticsSlice';
import { useAIPurchaseDateRangeParams } from './hooks/useAIPurchaseDateRangeParams';
import AIPurchaseDateRangeControls from './components/AIPurchaseDateRangeControls';
import AIPurchaseBreadcrumbs from './components/AIPurchaseBreadcrumbs';
import OverallSummaryCard from './components/OverallSummaryCard';
import BrandBreakdownGrid from './components/BrandBreakdownGrid';

/**
 * Page 2 — per-brand AI purchase adoption breakdown within one state. Shows the
 * cumulative totals/percentages for the state at the top (re-using `by-state`
 * filtered to this one state), then the per-brand cards below, each navigating to
 * the store breakdown page for that brand.
 */
export default function AIPurchaseNumbersStatePage() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { state: encodedState } = useParams<{ state: string }>();
    const state = decodeURIComponent(encodedState ?? '');

    const { fromDate, toDate, queryString, setDateRange } = useAIPurchaseDateRangeParams();

    const states = useAIPurchaseStates();
    const loading = useAIPurchaseLoading();
    const error = useAIPurchaseError();

    useEffect(() => {
        if (!state) return;
        dispatch(
            fetchAIPurchaseByState({ state: state as AIPurchaseAnalyticsState, fromDate, toDate })
        );
    }, [dispatch, state, fromDate, toDate]);

    const stateSummary = useMemo(
        () => states.find((item) => item.state === state) ?? null,
        [states, state]
    );

    const handleSelectBrand = (brandId: string, brandName: string) => {
        navigate(
            `/ai-purchase-numbers/state/${encodeURIComponent(state)}/brand/${brandId}?${queryString}`,
            { state: { brandName } }
        );
    };

    return (
        <div className="w-full space-y-6">
            <AIPurchaseBreadcrumbs
                items={[
                    { label: 'AI Purchase Adoption', to: `/ai-purchase-numbers?${queryString}` },
                    { label: state },
                ]}
            />

            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-4xl font-bold text-[#1e293b] leading-tight">{state}</h2>
                    <p className="text-lg text-[#64748b] mt-2">
                        AI purchase adoption by brand for the selected date range.
                    </p>
                </div>

                <AIPurchaseDateRangeControls
                    fromDate={fromDate}
                    toDate={toDate}
                    onApply={setDateRange}
                />
            </div>

            <OverallSummaryCard
                title={`${state} — Overall`}
                description="Cumulative AI purchase adoption across every brand in this state."
                overall={stateSummary}
                loading={loading.states === 'loading'}
                error={error.states}
            />

            <div>
                <h3 className="text-lg font-semibold text-[#1e293b] mb-3">By brand</h3>
                <BrandBreakdownGrid
                    state={state}
                    fromDate={fromDate}
                    toDate={toDate}
                    onSelectBrand={handleSelectBrand}
                />
            </div>
        </div>
    );
}
